import React, { useState } from 'react';
import { Info, Loader2, Upload, X, ExternalLink } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  ScrollableDialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ColorPicker from '@/components/admin/ordering-page/ColorPicker';
import { FileUploadDropzone } from '@/components/media/FileUploadDropzone';
import { useRestaurantImages } from '@/hooks/useRestaurantMedia';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface Restaurant {
  restaurant_id: string;
  restaurant_name: string;
  address: string | null;
  status: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  ubereats_commission_rate: number | null;
  ubereats_markup: number | null;
  google_review_url: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  ordering_page_url: string | null;
  review_page_url: string | null;
  organisation_id: string | null;
}

interface RestaurantEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurant: Restaurant | null;
  organisationId: string;
  onSave?: () => void;
}

const RestaurantEditDialog: React.FC<RestaurantEditDialogProps> = ({
  open,
  onOpenChange,
  restaurant,
  organisationId,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    primary_color: restaurant?.primary_color || '#c5b068',
    secondary_color: restaurant?.secondary_color || '#890c17',
    logo_url: restaurant?.logo_url || '',
    ubereats_commission_rate: restaurant?.ubereats_commission_rate || 0,
    ubereats_markup: restaurant?.ubereats_markup || 0,
    google_review_url: restaurant?.google_review_url || '',
    facebook_url: restaurant?.facebook_url || '',
    instagram_url: restaurant?.instagram_url || '',
  });
  const [googleUrlError, setGoogleUrlError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Hook for image upload
  const { uploadImageAsync, isUploading } = useRestaurantImages(organisationId);

  // Update form data when restaurant changes
  React.useEffect(() => {
    if (restaurant) {
      setFormData({
        primary_color: restaurant.primary_color || '#c5b068',
        secondary_color: restaurant.secondary_color || '#890c17',
        logo_url: restaurant.logo_url || '',
        ubereats_commission_rate: restaurant.ubereats_commission_rate || 0,
        ubereats_markup: restaurant.ubereats_markup || 0,
        google_review_url: restaurant.google_review_url || '',
        facebook_url: restaurant.facebook_url || '',
        instagram_url: restaurant.instagram_url || '',
      });
      // Reset file selection when dialog opens with new restaurant
      setSelectedFile(null);
      setDropzoneKey(prev => prev + 1);
      setGoogleUrlError(null);
    }
  }, [restaurant]);

  // Strip protocol (http:// or https://) and trailing slashes from URL for storage
  const stripProtocol = (url: string): string => {
    if (!url) return '';
    return url.replace(/^https?:\/\//i, '').replace(/\/+$/, '').trim();
  };

  // Validate Google Review URL has g.page prefix
  const validateGoogleReviewUrl = (url: string): boolean => {
    if (!url) return true; // Empty is valid
    const stripped = stripProtocol(url);
    if (!stripped) return true;
    // Check if URL contains g.page
    return stripped.toLowerCase().includes('g.page');
  };

  // Handle Google Review URL change with validation
  const handleGoogleUrlChange = (value: string) => {
    setFormData({ ...formData, google_review_url: value });
    if (value && !validateGoogleReviewUrl(value)) {
      setGoogleUrlError('Google Review URL must include "g.page" (e.g., https://g.page/your-business)');
    } else {
      setGoogleUrlError(null);
    }
  };

  // Handle social URL change with protocol stripping on blur
  const handleSocialUrlBlur = (field: 'facebook_url' | 'instagram_url') => {
    const stripped = stripProtocol(formData[field]);
    setFormData({ ...formData, [field]: stripped });
  };

  // Review Page Info Popover component
  const ReviewPageInfoPopover = () => (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center text-blue-600 hover:text-blue-800 ml-1">
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div>
            <h4 className="font-medium">Review Page Not Configured</h4>
            <p className="text-sm text-muted-foreground">
              The review page URL is generated when you set up Pump'd review management for this restaurant.
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Request reviews via QR codes on receipts, Email and SMS</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Handle negative feedback before it hits the web</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Send 5-star reviewers to your Google Business Profile</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  // Handle logo file selection
  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  // Handle logo upload
  const handleLogoUpload = async () => {
    if (!selectedFile || !restaurant) return;

    try {
      // Generate filename with Logo-{timestamp} convention
      const timestamp = Date.now();
      const extension = selectedFile.name.split('.').pop() || 'png';
      const filename = `Logo-${timestamp}.${extension}`;

      // Create a new File with the renamed filename
      const renamedFile = new File([selectedFile], filename, { type: selectedFile.type });

      const result = await uploadImageAsync({
        file: renamedFile,
        organisation_id: organisationId,
        restaurant_id: restaurant.restaurant_id,
        tags: ['logo'],
      });

      // Update the logo URL in form data
      if (result?.public_url) {
        setFormData(prev => ({ ...prev, logo_url: result.public_url }));
        setSelectedFile(null);
        setDropzoneKey(prev => prev + 1);
        toast({
          title: 'Logo uploaded',
          description: 'Your new logo has been uploaded successfully.',
        });
      }
    } catch (error: any) {
      console.error('Logo upload failed:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload logo. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setDropzoneKey(prev => prev + 1);
  };

  // Handle save to Supabase
  const handleSave = async () => {
    if (!restaurant) return;

    // Validate Google Review URL before saving
    if (formData.google_review_url && !validateGoogleReviewUrl(formData.google_review_url)) {
      setGoogleUrlError('Google Review URL must include "g.page" (e.g., https://g.page/your-business)');
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        logo_url: formData.logo_url || null,
        ubereats_commission_rate: formData.ubereats_commission_rate,
        ubereats_markup: formData.ubereats_markup,
        google_review_url: formData.google_review_url || null,
        facebook_url: formData.facebook_url ? stripProtocol(formData.facebook_url) : null,
        instagram_url: formData.instagram_url ? stripProtocol(formData.instagram_url) : null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('restaurants_master')
        .update(updateData)
        .eq('restaurant_id', restaurant.restaurant_id)
        .eq('organisation_id', organisationId);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Changes saved',
        description: 'Restaurant settings have been updated successfully.',
      });

      // Call onSave callback to refresh the list
      if (onSave) {
        onSave();
      }

      // Close the dialog
      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to save restaurant:', error);
      toast({
        title: 'Save failed',
        description: error.message || 'Failed to save changes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!restaurant) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent className="sm:max-w-[700px]" aria-describedby="edit-restaurant-description">
        <DialogHeader>
          <DialogTitle>Edit Restaurant</DialogTitle>
          <DialogDescription id="edit-restaurant-description">
            Update restaurant branding, commission rates, and URLs.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Restaurant ID Info Box */}
          <div className="p-3 bg-muted rounded-md">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-primary mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground">
                  Restaurant ID: <span className="font-mono">{restaurant.restaurant_id}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Restaurant IDs cannot be changed.
                </p>
              </div>
            </div>
          </div>

          {/* Branding Section */}
          <div>
            <h4 className="text-sm font-medium mb-3">Branding</h4>
            <div className="space-y-4">
              {/* Logo Section */}
              <div className="space-y-3">
                <label className="text-sm font-medium">Logo</label>

                {/* Current Logo Preview */}
                {formData.logo_url && (
                  <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-md">
                    <img
                      src={formData.logo_url}
                      alt={restaurant.restaurant_name}
                      className="h-16 w-16 object-contain rounded-md border bg-white"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Current Logo</p>
                      <p className="text-xs text-muted-foreground">
                        Upload a new logo below to replace
                      </p>
                    </div>
                  </div>
                )}

                {/* File Selected Preview */}
                {selectedFile && (
                  <div className="flex items-center gap-4 p-3 bg-primary/5 border border-primary/20 rounded-md">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="New logo preview"
                      className="h-16 w-16 object-contain rounded-md border bg-white"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">New Logo Selected</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleRemoveFile}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleLogoUpload}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* File Upload Dropzone */}
                {!selectedFile && (
                  <FileUploadDropzone
                    key={dropzoneKey}
                    onFileSelect={handleFileSelect}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    maxSize={5}
                    multiple={false}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Color Pickers in 2-column grid on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorPicker
                  label="Primary Color"
                  value={formData.primary_color}
                  onChange={(color) => setFormData({ ...formData, primary_color: color })}
                />
                <ColorPicker
                  label="Secondary Color"
                  value={formData.secondary_color}
                  onChange={(color) => setFormData({ ...formData, secondary_color: color })}
                />
              </div>
            </div>
          </div>

          {/* Commission Rates Section */}
          <div>
            <h4 className="text-sm font-medium mb-3">Commission Rates</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* UberEats Commission Rate */}
              <div className="space-y-2">
                <label htmlFor="ubereats_commission_rate" className="text-sm font-medium">
                  UberEats Commission Rate (%)
                </label>
                <Input
                  id="ubereats_commission_rate"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.ubereats_commission_rate}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 100) {
                      setFormData({ ...formData, ubereats_commission_rate: value });
                    } else if (e.target.value === '') {
                      setFormData({ ...formData, ubereats_commission_rate: 0 });
                    }
                  }}
                  className="w-full"
                />
              </div>

              {/* UberEats Markup */}
              <div className="space-y-2">
                <label htmlFor="ubereats_markup" className="text-sm font-medium">
                  UberEats Markup (%)
                </label>
                <Input
                  id="ubereats_markup"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.ubereats_markup}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 100) {
                      setFormData({ ...formData, ubereats_markup: value });
                    } else if (e.target.value === '') {
                      setFormData({ ...formData, ubereats_markup: 0 });
                    }
                  }}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* URLs Section */}
          <div>
            <h4 className="text-sm font-medium mb-3">URLs</h4>
            <div className="space-y-4">
              {/* Google Review URL */}
              <div className="space-y-2">
                <label htmlFor="google_review_url" className="text-sm font-medium">
                  Google Review URL
                </label>
                <Input
                  id="google_review_url"
                  type="text"
                  placeholder="https://g.page/your-business/review"
                  value={formData.google_review_url}
                  onChange={(e) => handleGoogleUrlChange(e.target.value)}
                  className={`w-full ${googleUrlError ? 'border-red-500' : ''}`}
                />
                {googleUrlError && (
                  <p className="text-xs text-red-500">{googleUrlError}</p>
                )}
              </div>

              {/* Social URLs - 2 column grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Facebook URL */}
                <div className="space-y-2">
                  <label htmlFor="facebook_url" className="text-sm font-medium">
                    Facebook URL
                  </label>
                  <Input
                    id="facebook_url"
                    type="text"
                    placeholder="facebook.com/your-page"
                    value={formData.facebook_url}
                    onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                    onBlur={() => handleSocialUrlBlur('facebook_url')}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    The https:// prefix will be stripped automatically
                  </p>
                </div>

                {/* Instagram URL */}
                <div className="space-y-2">
                  <label htmlFor="instagram_url" className="text-sm font-medium">
                    Instagram URL
                  </label>
                  <Input
                    id="instagram_url"
                    type="text"
                    placeholder="instagram.com/your-handle"
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                    onBlur={() => handleSocialUrlBlur('instagram_url')}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    The https:// prefix will be stripped automatically
                  </p>
                </div>
              </div>

              {/* Read-only URLs - 2 column grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ordering Page URL - Read Only */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Ordering Page URL
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={restaurant?.ordering_page_url || ''}
                      readOnly
                      className="w-full bg-muted cursor-not-allowed"
                    />
                    {restaurant?.ordering_page_url && (
                      <a
                        href={restaurant.ordering_page_url.startsWith('http') ? restaurant.ordering_page_url : `https://${restaurant.ordering_page_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This URL is configured in domain settings
                  </p>
                </div>

                {/* Review Page URL - Read Only */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center">
                    Review Page URL
                    {!restaurant?.review_page_url && <ReviewPageInfoPopover />}
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={restaurant?.review_page_url || 'Not configured'}
                      readOnly
                      className={`w-full bg-muted cursor-not-allowed ${!restaurant?.review_page_url ? 'text-muted-foreground' : ''}`}
                    />
                    {restaurant?.review_page_url && (
                      <a
                        href={restaurant.review_page_url.startsWith('http') ? restaurant.review_page_url : `https://${restaurant.review_page_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This URL is configured in review management
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !!googleUrlError}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </ScrollableDialogContent>
    </Dialog>
  );
};

export default RestaurantEditDialog;
